import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Spin, message } from "antd";
import { UseLazyApiCallV2 } from "hooks/use-lazy-api-call-v2";
import { useCurrentUserContext } from "providers";
import {
  AttachFileIcon,
  CancelSelectedFile,
  FileCancelIcon,
  UploadIcon,
} from "icons";
import { useLanguageTranslator } from "hooks/use-language-translator";
import styles from "./styles.module.scss";
import { ConditionalRenderer } from "components";
import { FILE_EXTENSION_ICONS } from "pages/notes/constants";

export const FileUpload = ({
  note_id,
  refetchnoteAttachments,
  uploading,
  getFileUuidsFromChild,
  setUploading,
  showUploadedSection,
}) => {
  const { translator } = useLanguageTranslator();
  const uploadFileTrans = translator("caseDetail.uploadFiles");
  const [fileDragged, setFileDragged] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const [binaryFile, setBinaryFile] = useState("");
  const [binaryFiles, setBinaryFiles] = useState([]);
  const [signedUrl, setSignedUrl] = useState("");
  const [signedUrls, setSignedUrls] = useState([]);
  const [key, setKey] = useState("");

  const { user } = useCurrentUserContext();
  // const [uploading, setUploading] = useState(false);

  // === API HOOKS ===
  const [uploadSuccess] = UseLazyApiCallV2({
    url: "/add_attatchment_metadata",
    method: "POST",
  });

  const [uploadFile] = UseLazyApiCallV2({
    url: "/get_multiple_upload_url",
    method: "POST",
  });

  // const [uploadFile] = UseLazyApiCallV2({
  //   url: "/add_note_attatchment",
  //   method: "POST",
  // });

  // Prevent multiple uploads
  const uploadRef = useRef(false);

  // ---- Upload when BOTH binaryFile + signedUrl ready (only ONCE) ----
  useEffect(() => {
    if (
      binaryFiles?.length > 0 &&
      signedUrls?.length > 0 &&
      !uploadRef.current
    ) {
      uploadRef.current = true; // prevent double upload
      uploadOnBucket();
    }
  }, [binaryFiles, signedUrls]);

  useEffect(() => {
    if (uploading) {
      startUploading({ files: selectedFiles });
    }
  }, [uploading]);

  // ---- Step 1: Start Uploading ----
  function startUploading({ files }) {
    uploadRef.current = false; // reset flag for new upload
    setSelectedFiles(files);

    handleUpload(files);
  }

  const handleFileChange = (event) => {
    const files = event.target.files;
    const data = Object.entries(files);
    const arrOfFiles = data?.map((item) => item[1]);

    setSelectedFiles(arrOfFiles);

    // const file = event.target.files?.[0];
    // if (arrOfFiles?.length > 0) startUploading({ files: arrOfFiles });
  };

  // ---- Step 2: Get Signed URL + Read File ----
  async function handleUpload(files) {
    if (files?.length < 1) {
      setUploading(false);
      getFileUuidsFromChild([]);
      return;
    }

    // setUploading(true);
    const filenames = files?.map((item) => item.name);

    if (files?.length > 0) {
      files.forEach((item) => {
        const reader = new FileReader(); // ✅ new instance per file

        reader.onload = () => {
          setBinaryFiles((prev) => [
            ...prev,
            {
              name: item.name,
              binaryData: reader.result,
              fileType: item?.type,
            },
          ]);
        };

        reader.onerror = (e) => {
          console.error("File read error:", e);
        };

        reader.readAsArrayBuffer(item);
      });
    }

    try {
      const response = await uploadFile({
        params: {
          filenames,
          note_id,
        },
      });

      if (response?.status === "Success") {
        setSignedUrls(response?.files);
        // setKey(response.key);
      }
    } catch (error) {
      setAllStatesToDefault();
    }
  }

  async function uploadSingleFile({
    binaryData,
    fileName,
    fileType,
    signedUrl,
    key,
  }) {
    await axios.put(signedUrl, binaryData, {
      headers: {
        "Content-Type": fileType,
      },
      withCredentials: true,
    });

    // if (response.status === 200) {
    //   await uploadSuccess({
    //     params: {
    //       loggedUser: user?.email,
    //       note_id,
    //       key,
    //       filename: fileName,
    //     },
    //   });
    // }
  }

  // ---- Step 3: Upload to S3 (PUT) ----
  async function uploadOnBucket() {
    const signedUrlMap = new Map(
      signedUrls.map((item) => [item.filename, item]),
    );
    try {
      // setUploading(true);

      for (const file of binaryFiles) {
        const signedData = signedUrlMap.get(file.name);

        if (!signedData) {
          console.warn("No signed URL for:", file.name);
          continue;
        }

        await uploadSingleFile({
          binaryData: file.binaryData,
          fileName: file.name,
          fileType: file.fileType, // make sure you stored this
          signedUrl: signedData.url,
          key: signedData.key,
        });
      }
      const uuids = signedUrls?.map((item) => item.key);
      getFileUuidsFromChild(uuids);
      message.success(
        selectedFiles?.length > 1
          ? uploadFileTrans.filesUploadedSuccess
          : uploadFileTrans.uploadedSuccess,
      );
      setAllStatesToDefault();
      // await refetchnoteAttachments();
    } catch (error) {
      console.error(error);
      message.error(error?.message || "Upload failed");
    } finally {
      // setUploading(false);
    }
  }

  // ---- Reset All State ----
  function setAllStatesToDefault() {
    setSelectedFiles([]);
    setBinaryFiles([]);
    setSignedUrls([]);
    setSelectedFile(null);
    setBinaryFile("");
    setSignedUrl("");
    uploadRef.current = false;
    setUploading(false);

    const fileInput = document.getElementById("file-input");
    if (fileInput) fileInput.value = "";
  }

  // ---- Browse Click ----
  function handleClickBrowse() {
    setAllStatesToDefault();
    document.getElementById("file-input").click();
  }

  // ---- Drag & Drop ----
  function handleDropFile(e) {
    e.preventDefault();
    e.stopPropagation(); // IMPORTANT: prevents duplicate triggers

    const file = e.dataTransfer.files?.[0];
    if (file) startUploading({ file });
  }

  function handleDragOver(e) {
    e.preventDefault();
  }

  function handleUnselectFile(file) {
    const filtered = selectedFiles?.filter((item) => item.name !== file.name);
    setSelectedFiles(filtered);
  }

  function handleClickUpload() {}

  return (
    <Spin spinning={uploading}>
      <div
        className={`${styles.uploadContainer}`}
        // onDrop={handleDropFile}
        // onDragOver={handleDragOver}
        id="file-upload"
        style={{
          position: "relative",
          height: showUploadedSection ? "50%" : "90%",
        }}
        onDragEnter={() => setFileDragged(true)}
        onDragLeave={() => setFileDragged(false)}
      >
        <div className={styles.upload}>
          {/* <UploadIcon /> */}
          <div className={styles.labels}>
            <div className={styles.labelAndLength}>
              <p className={styles.label}>{uploadFileTrans.uploadBtnText}</p>
              <ConditionalRenderer condition={selectedFiles?.length > 0}>
                <div className={styles.length}>{selectedFiles?.length}</div>
              </ConditionalRenderer>
            </div>
            <div className={styles.compatibility}>
              {uploadFileTrans.compatible}
            </div>
          </div>

          <button onClick={handleClickBrowse} type="button">
            {/* {uploadFileTrans.uploadBtnText} */}
            <AttachFileIcon />
            {uploadFileTrans.attachFiles}
          </button>
        </div>

        <input
          style={{ display: "none" }}
          id="file-input"
          type="file"
          onChange={handleFileChange}
          accept=".pdf, .docx, .png, .jpg, .txt"
          multiple
        />
        <div
          className={styles.selectedFiles}
          style={{ height: showUploadedSection ? "250px" : "100%" }}
        >
          {selectedFiles?.map((item) => {
            const fileExtension = item?.name?.split(".")[1];
            const FileExtensionIcon = FILE_EXTENSION_ICONS[fileExtension];

            return (
              <div key={item?.name} className={styles.singleFile}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "5px" }}
                >
                  <FileExtensionIcon />
                  <div>{item?.name}</div>
                </div>
                <CancelSelectedFile
                  onClick={() => handleUnselectFile(item)}
                  style={{ cursor: "pointer", userSelect: "none" }}
                />
              </div>
            );
          })}
        </div>

        {/* <Spin spinning={uploading}>
        <div className={styles.uploadBtn}>
          <button
            onClick={() => startUploading({ files: selectedFiles })}
            type="button"
          >
            Upload
          </button>
        </div>
      </Spin> */}
      </div>
    </Spin>
  );
};
